import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Trash2, RotateCcw, AlertCircle } from "lucide-react";
import { QuizOptions } from "../components/QuizOptions";
import { MistakeExplain } from "../components/MistakeExplain";

const SOURCE_LABEL = {
  "topic-quiz": "Topic quiz",
  "auto-quiz": "Lesson mini-quiz",
  "custom-quiz": "Teacher quiz",
};

export default function Mistakes() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [reveal, setReveal] = useState({});
  const [choice, setChoice] = useState({});
  const [filter, setFilter] = useState("due"); // due | all
  const [err, setErr] = useState(null);

  const load = useCallback(() => api.get(`/me/mistakes${filter === "due" ? "?due=true" : ""}`).then((r) => setItems(r.data.mistakes)).catch((e) => setErr(apiError(e, "Could not load mistakes"))), [filter]);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading) return <div className="text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const remove = async (id) => {
    try {
      await api.delete(`/me/mistakes/${id}`);
      load();
    } catch (e) {
      setErr(apiError(e, "Could not dismiss"));
    }
  };

  const check = async (m) => {
    setReveal({ ...reveal, [m.id]: true });
    const picked = choice[m.id];
    try {
      await api.post(`/me/mistakes/${m.id}/review`, { is_correct: picked === m.correct_index });
    } catch (e) {
      setErr(apiError(e, "Could not save your review"));
    }
  };

  const dueCount = items.filter((m) => m.is_due).length;

  return (
    <div className="space-y-8" data-testid="mistakes-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs uppercase tracking-widest text-red-300">
          <AlertCircle size={12} /> Practice mistakes · Spaced repetition
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Fix what tripped you up.</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">Get one right and the interval doubles (1d → 3d → 7d → 14d → 30d). Three streaks in a row and it's gone.</p>
        <div className="mt-4 flex items-center gap-2 text-xs">
          <button onClick={() => setFilter("due")} data-testid="filter-due" className={`rounded-md border px-3 py-1 ${filter === "due" ? "border-red-400/50 bg-red-500/10 text-red-200" : "border-white/10 bg-white/5 text-slate-300"}`}>
            Due today ({dueCount})
          </button>
          <button onClick={() => setFilter("all")} data-testid="filter-all" className={`rounded-md border px-3 py-1 ${filter === "all" ? "border-cyan-400/50 bg-cyan-500/10 text-cyan-200" : "border-white/10 bg-white/5 text-slate-300"}`}>
            All saved
          </button>
        </div>
      </div>

      {err && <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="mistakes-error">{err}</div>}

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 bg-black/20 p-10 text-center text-sm text-slate-500" data-testid="mistakes-empty">
          {filter === "due" ? "🎉 Nothing due right now. Check back tomorrow." : "Clean slate — no mistakes saved yet."}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => {
            const revealed = !!reveal[m.id];
            const picked = choice[m.id];
            const graded = revealed && picked != null;
            const nextDate = m.next_review_at ? new Date(m.next_review_at).toLocaleDateString() : "-";
            return (
              <div key={m.id} className={`rounded-md border ${m.is_due ? "border-red-400/40 bg-red-500/5" : "border-white/10 bg-black/40"} p-5`} data-testid={`mistake-${m.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-widest">
                  <div className="flex flex-wrap items-center gap-2 text-red-300">
                    <span>{SOURCE_LABEL[m.source] || m.source} · {m.source_id}</span>
                    <span className="rounded border border-white/10 bg-black/60 px-2 py-0.5 font-mono-num text-slate-400">streak {m.correct_streak || 0}</span>
                    <span className="font-mono-num text-slate-500">next: {nextDate}</span>
                  </div>
                  <button onClick={() => remove(m.id)} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-slate-400 hover:text-red-300" data-testid={`mistake-remove-${m.id}`}>
                    <Trash2 size={12} className="inline" /> Dismiss
                  </button>
                </div>
                <div className="font-display mt-2 text-lg font-semibold text-white">{m.q}</div>
                <QuizOptions options={m.options} chosen={picked} correctIndex={m.correct_index} graded={graded} compact onChoose={(oi) => setChoice({ ...choice, [m.id]: oi })} testId={(oi) => `mistake-${m.id}-opt-${oi}`} />
                <div className="mt-3 flex items-center gap-3">
                  {!revealed ? (
                    <button onClick={() => check(m)} disabled={picked == null} data-testid={`mistake-check-${m.id}`} className="rounded-md bg-lime-500 px-3 py-1 text-xs font-semibold text-black hover:bg-lime-400 disabled:opacity-50">
                      Check answer
                    </button>
                  ) : (
                    <button
                      onClick={() => { setReveal({ ...reveal, [m.id]: false }); setChoice({ ...choice, [m.id]: undefined }); load(); }}
                      className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-3 py-1 text-xs"
                    >
                      <RotateCcw size={12} /> Next
                    </button>
                  )}
                  {graded && m.explanation && (
                    <span className="text-xs text-slate-400">{m.explanation}</span>
                  )}
                </div>
                {(graded || m.ai_explanation) && <MistakeExplain mistake={m} />}
              </div>
            );
          })}
        </div>
      )}

      <div className="text-center">
        <Link to="/dashboard" className="text-xs text-slate-400 hover:text-cyan-300">← Back to dashboard</Link>
      </div>
    </div>
  );
}
